import { get } from '../utils/networking';

export interface UserProfile {
  userId: number;
  email: string;
  auth0Id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  isPending: boolean;
  tenantId: number;
  tenantName: string;
}

export interface TeamMembership {
  teamId: number;
  teamName: string;
  organizationName: string;
  isLeader: boolean;
  role: string;
  joinedAt: string;
}

export interface OrganizationRole {
  role: string;
  assignedAt: string;
}

interface SurveyRef {
  surveyId: number;
  surveyTitle: string;
}

export type PlanMeasureStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

export interface ResponsibleMeasure extends SurveyRef {
  measureId: number;
  title: string;
  description: string | null;
  status: PlanMeasureStatus;
  dueDate: string;
}

export interface MeasureProposal extends SurveyRef {
  proposalId: number;
  title: string;
  reason: string | null;
  createdAt: string;
}

export interface PlanComment extends SurveyRef {
  commentId: number;
  text: string;
  createdAt: string;
}

export interface ApprovedPlan extends SurveyRef {
  planId: number;
  approvedAt: string;
  deviation: string | null;
}

export interface AreaDecision extends SurveyRef {
  decisionId: number;
  decision: 'PLAN_LATER' | 'NOT_NOW' | 'NEXT_SURVEY' | 'ACCEPT';
  reason: string | null;
  decidedAt: string;
}

export interface WorkEnvironmentData {
  responsibleForMeasures: ResponsibleMeasure[];
  proposals: MeasureProposal[];
  planComments: PlanComment[];
  approvedPlans: ApprovedPlan[];
  areaDecisions: AreaDecision[];
}

export interface GuideAccess {
  guideId: number;
  title?: string | null;
  accessGrantedAt: string;
}

export interface ActivityLogEntry {
  action: string;
  timestamp: string;
  teamName?: string;
  surveyTitle?: string;
  details?: any;
}

export interface PrivacyInfo {
  dataRetentionPolicy: string;
  legalBasis: string;
  processingPurpose: string;
  dataRecipients: string[];
  dataSource: string;
  rightsAvailable: {
    correction: string;
    deletion: string;
    restriction: string;
    portability: string;
    objection: string;
  };
}

export interface AnonymizedDataNote {
  note: string;
}

export interface UserDataExport {
  exportedAt: string;
  userId: number;
  profile: UserProfile;
  teams: TeamMembership[];
  roles: OrganizationRole[];
  workEnvironment: WorkEnvironmentData;
  guides: GuideAccess[];
  activityLog: ActivityLogEntry[];
  privacyInfo: PrivacyInfo;
  anonymizedData: AnonymizedDataNote;
}

export async function fetchUserDataExport(): Promise<UserDataExport> {
  return get<UserDataExport>('/api/gdpr/export');
}
